import { createModalOptions } from 'modals/modal-options-factory';
import { useDispatch, useSelector } from 'react-redux';
import { toggleModal } from 'state/actions';
import {
  createService,
  updateService,
  deleteService,
  SERVICES_API_ERRORS
} from 'state/callers/services';
import { servicesWithExistedReposOnlySelector } from 'state/selectors/services';
import ServiceForm from './ServiceForm/ServiceForm';
import _ from 'lodash';
import useToast from 'components/hooks/useToast';
import useConfirmModal from 'components/hooks/useConfirmModal';

const useServices = () => {
  const dispatch = useDispatch();
  const services = useSelector(servicesWithExistedReposOnlySelector);
  const toast = useToast();
  const confirmModal = useConfirmModal();

  const handleApiError = apiError => {
    let message = '';
    switch (apiError) {
      case SERVICES_API_ERRORS.NAME_EXISTS:
        message = 'Service name already exists';
        break;
      case SERVICES_API_ERRORS.UNKNOWN:
      default:
        message = 'Oops.. something went wrong.';
    }
    toast({ message, variant: 'error' });
  };

  const onSubmit = async service => {
    const isEditMode = !!service.id;
    const apiMethod = isEditMode ? updateService : createService;

    const serviceRes = await apiMethod(service)(dispatch);
    if (serviceRes.error) return handleApiError(serviceRes.error);

    dispatch(toggleModal());
    toast({
      message: `Service ${service.name} was ${
        isEditMode ? 'updated' : 'created'
      } successfully`,
      variant: 'success'
    });
  };

  const onDelete = async service => {
    const isConfirmed = await confirmModal({
      description: `Are you sure you want to delete ${service.name}?`,
      confirmText: 'Yes, Delete'
    });
    if (!isConfirmed) return;

    const res = await deleteService(service.id)(dispatch);
    if (res.error) return handleApiError(res);

    toast({
      message: `Service ${service.name} was deleted successfully`,
      variant: 'success'
    });
  };

  const toggleServiceFormModal = service => {
    dispatch(
      toggleModal(
        createModalOptions({
          content: () => (
            <ServiceForm
              service={_.cloneDeep(service)}
              onSubmit={onSubmit}
              onCancel={() => dispatch(toggleModal())}
            />
          ),
          size: 'medium',
          height: '46rem'
        })
      )
    );
  };

  return {
    services,
    toggleServiceFormModal,
    onSubmit,
    onDelete
  };
};

export default useServices;
